import React, { Component } from 'react'

import axios from 'axios'
import { AUTO3P_BACKEND_URL } from 'config'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'

import { APPLICATION_KEY } from '@/utils/config'
import withRouter from '@/utils/helpers/withRouter'

import Button from '@/ui/Button/Button'
import Flex from '@/ui/Flex'
import Heading from '@/ui/Heading'
import Text from '@/ui/Text'

import { REPORT_ERROR_PATH } from '@/services'

const errorWrapperStyle = {
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
}

const ErrorFallback = ({ errorName, onRetry }) => {
  const { formatMessage } = useIntl()

  return (
    <Flex {...errorWrapperStyle}>
      <Heading>{formatMessage({ id: 'error.handler.title' })}</Heading>
      <Text>{errorName}</Text>
      <Button onClick={onRetry} size='sm' labelId='login.try_again' />
    </Flex>
  )
}

ErrorFallback.propTypes = {
  errorName: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
}

const handleChunkLoadError = () => {
  const didTryToRefreshApplication = window.sessionStorage.getItem(
    'didTryToRefreshApplication',
  )

  if (didTryToRefreshApplication) {
    return
  }

  window.sessionStorage.setItem('didTryToRefreshApplication', true)
  window.location.reload(true)
}

class ErrorHandler extends Component {
  constructor(props) {
    super(props)
    this.state = { error: false, errorInfo: null, hasError: false }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
      hasError: true,
    })

    if (error.name === 'ChunkLoadError') {
      handleChunkLoadError()
      return
    }

    this.sendErrorReport(error)
  }

  isLocalhost() {
    return (
      typeof window !== 'undefined' &&
      ['localhost', '127.0.0.1', '::1'].includes(window.location?.hostname)
    )
  }

  sendErrorReport(error) {
    if (this.isLocalhost()) return

    try {
      axios.post(`${AUTO3P_BACKEND_URL}${REPORT_ERROR_PATH}`, {
        appKey: APPLICATION_KEY,
        appVer: VERSION,
        msgCode: error?.name ?? 'JavascriptError',
        msgHeader: error?.message ?? 'Auto3p front-end application error',
        msgText: this.buildErrorReport(error),
      })
    } catch (e) {
      console.error(e)
    }
  }

  buildErrorReport(error) {
    return [
      `Location: ${this.clearHttp(window.location?.href)}`,
      `UserAgent: ${window.navigator?.userAgent}`,
      `Message: ${error?.message}`,
      `Stack: ${this.clearHttp(error?.stack)}`,
    ].join('\n')
  }

  clearHttp(value) {
    return value
      ? value.replaceAll('http://', '').replaceAll('https://', '')
      : ''
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      prevProps?.router?.location?.pathname !==
        this.props?.router?.location?.pathname
    ) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          errorName={this.state?.error?.name?.toString()}
          onRetry={() => this.setState({ hasError: false })}
        />
      )
    }
    return this.props.children
  }
}

export default withRouter(ErrorHandler)

ErrorHandler.propTypes = {
  children: PropTypes.any,
  router: PropTypes.object,
}
